import { ListAltOutlined } from '@mui/icons-material';
import { CircularProgress, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Permission } from '@procureai/permissions';
import { Restricted } from '@procureai/permissions-ui';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { I18nNamespace, NegotiationDomainType } from '~/common/types';
import {
  ColumnSelector,
  DataGridProStyled,
  DataGridWidgetHeader,
  DensityButton,
  ResetRemoteStateButton,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';
import { HoverMenuProvider } from '~/components/HoverMenu';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { useProcessStepsAutoRefresh } from '~/hooks/useProcessStepsAutoRefresh/useProcessStepsAutoRefresh';
import { ProcessStepsAutoRefreshVariant } from '~/hooks/useProcessStepsAutoRefresh/useProcessStepsAutoRefresh.util';

import { DemandCaseAdd } from './components/DemandCaseAdd/DemandCaseAdd';
import { useDemandCasesDataGridConfig } from './DemandCasesWidget.dataGrid';
import { useDemandCasesWidget } from './DemandCasesWidget.hooks';

const domain = NegotiationDomainType.DEMAND_CASES;

export const DemandCasesWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandCasesWidget' });

  const apiRef = useGridApiRef();
  const config = useDemandCasesDataGridConfig();

  const {
    loading: loadingRemoteState,
    gridProps,
    onDensityChange,
    onRemoteStateReset,
  } = useDataGridRemoteState({
    apiRef,
    config,
    domain,
  });

  const { isActiveCompetitiveBidding, status, rows, onRowClick, loading } = useDemandCasesWidget({
    demandId: entityId,
    t,
  });

  const skip = loadingRemoteState ?? loading;

  useProcessStepsAutoRefresh({
    variant: ProcessStepsAutoRefreshVariant.DEMAND_NEGOTIATION_CASE_LIST,
  });

  return (
    <Widget>
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<ListAltOutlined fontSize="small" />} />}
        actionsLeft={skip ? <CircularProgress size={20} /> : undefined}
        actionsRight={
          <Restricted needs={{ permission: Permission.DemandCreateCase }}>
            <DemandCaseAdd
              status={status}
              negotiationCaseCount={rows.length}
              isActiveCompetitiveBidding={isActiveCompetitiveBidding}
            />
          </Restricted>
        }
      />
      <WidgetContent>
        {skip ? null : (
          <div>
            <DataGridWidgetHeader
              right={
                <Stack direction="row">
                  <DensityButton density={gridProps.density} onDensityChange={onDensityChange} />
                  <ColumnSelector
                    columns={gridProps.columns}
                    columnVisibilityModel={gridProps.columnVisibilityModel}
                    onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
                  />
                  <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
                </Stack>
              }
            />
            <HoverMenuProvider>
              <DataGridProStyled
                {...config}
                apiRef={apiRef}
                autoHeight
                hideFooter
                disableColumnMenu
                onRowClick={onRowClick}
                loading={loading}
                rows={rows}
                {...gridProps}
              />
            </HoverMenuProvider>
          </div>
        )}
      </WidgetContent>
    </Widget>
  );
};
